<template>
  <div class="w-full min-w-0">
    <div class="relative">
      <Popover :key="popoverKey" v-model:open="isOpen">
        <PopoverTrigger as-child>
          <Button v-bind="$attrs" :id="triggerId" role="combobox" variant="outline" :aria-expanded="isOpen" :aria-required="required" :class="triggerClass" @blur="onBlur" @focus="onFocus">
            {{ selectedLabel }}
            <ChevronDown class="absolute right-0 min-h-6 min-w-8 shrink-0 bg-light-bg-default stroke-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent class="max-h-[--radix-popover-content-available-height] w-[--radix-popover-trigger-width] rounded-none border-light-bd-muted bg-light-bg-default p-0 font-Spectral" @click.stop>
          <Command>
            <CommandInput placeholder="Search" />
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandList>
              <CommandGroup>
                <!-- 検索は value に対して掛かるため、表示名を渡す -->
                <CommandItem v-for="item in items" :key="item.option.value" :class="item.class" :data-testid="item.option.value" :value="item.option.label" @select="onSelect(item.option)">
                  <Check :class="item.checkClass" />
                  {{ item.option.label }}
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <label :class="labelClass" :for="triggerId">{{ label }}<span v-if="required" aria-hidden="true" class="ml-0.5 text-light-fg-danger">*</span></label>
    </div>
    <AtomsErrorText v-if="isError" class="mt-1 block" lang="en">{{ error }}</AtomsErrorText>
  </div>
</template>

<script setup lang="ts">
import { Check, ChevronDown } from '@lucide/vue';
import { tv } from 'tailwind-variants';
import { computed, ref, useId } from 'vue';

import AtomsErrorText from '@/components/atoms/ErrorText/ErrorText.vue';
import Button from '@/components/lib/shadcn-vue/button/Button.vue';
import Command from '@/components/lib/shadcn-vue/command/Command.vue';
import CommandEmpty from '@/components/lib/shadcn-vue/command/CommandEmpty.vue';
import CommandGroup from '@/components/lib/shadcn-vue/command/CommandGroup.vue';
import CommandInput from '@/components/lib/shadcn-vue/command/CommandInput.vue';
import CommandItem from '@/components/lib/shadcn-vue/command/CommandItem.vue';
import CommandList from '@/components/lib/shadcn-vue/command/CommandList.vue';
import Popover from '@/components/lib/shadcn-vue/popover/Popover.vue';
import PopoverContent from '@/components/lib/shadcn-vue/popover/PopoverContent.vue';
import PopoverTrigger from '@/components/lib/shadcn-vue/popover/PopoverTrigger.vue';
import type { Option, Options } from '@/models/option';

defineOptions({ inheritAttrs: false });

interface Props {
  label: string;
  options: Options;
  modelValue?: string | number;
  onUpdateModelValue?: (value: string | number) => void;
  required?: boolean;
  error?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  onUpdateModelValue: undefined,
  required: true,
  error: '',
});

const triggerId = useId();

const isOpen = ref(false);
const isFocused = ref(false);
// 選んだ直後に Popover が閉じないため、描画し直して閉じる
const popoverKey = ref(0);

const isError = computed(() => props.error !== '');
const selectedLabel = computed(() => props.options.find((option) => option.value === props.modelValue)?.label ?? '');
const isFloating = computed(() => isOpen.value || isFocused.value || selectedLabel.value !== '');

const onFocus = () => (isFocused.value = true);
const onBlur = () => (isFocused.value = false);

const onSelect = (option: Option) => {
  props.onUpdateModelValue?.(option.value);
  isOpen.value = false;
  popoverKey.value++;
};

const triggerStyle = tv({
  base: 'relative box-border flex h-12 w-full items-center justify-between overflow-hidden rounded-none border border-light-bd-muted bg-light-bg-default pl-4 pr-8 text-sm font-light text-light-fg-default outline-none transition-colors duration-100 ease-in-out focus:border-light-bd-accent',
  variants: {
    isError: {
      true: 'border-light-bd-danger focus:border-light-bd-danger',
    },
  },
});

const labelStyle = tv({
  base: 'pointer-events-none absolute left-3 max-w-[calc(100%-3rem)] -translate-y-1/2 truncate px-1 leading-4 transition-all duration-100 ease-in-out',
  variants: {
    isFloating: {
      true: 'top-0 bg-light-bg-default text-[11px] text-light-fg-accent',
      false: 'top-1/2 text-sm text-light-fg-muted',
    },
    isError: {
      true: '',
    },
  },
  compoundVariants: [{ isFloating: true, isError: true, class: 'text-light-fg-danger' }],
});

const itemStyle = tv({
  base: 'h-12',
  variants: {
    isSelected: {
      true: 'bg-light-bg-inset',
      false: 'bg-light-bg-default',
    },
    disabled: {
      true: 'pointer-events-none text-light-fg-disabled',
    },
  },
});

const triggerClass = computed(() => triggerStyle({ isError: isError.value }));
const labelClass = computed(() => labelStyle({ isFloating: isFloating.value, isError: isError.value }));
const items = computed(() =>
  props.options.map((option) => {
    const isSelected = option.value === props.modelValue;
    return {
      option,
      class: itemStyle({ isSelected, disabled: option.disabled === true }),
      checkClass: isSelected ? 'mr-2 h-4 w-4 opacity-100' : 'mr-2 h-4 w-4 opacity-0',
    };
  }),
);
</script>
